import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { makeTr } from '../services/trLookup';

const STORAGE_KEY = 'kiosk_shelf_codes_list';

// Offizielle Code-128B Bitmuster (Werte 0..106, jeweils Strich-/Lückenbreiten, Summe = 11 Module; Stop = 13 Module)
const CODE128_PATTERNS = [
  '212222','222122','222221','121223','121322','131222','122213','122312','132212','221213',
  '221312','231212','112232','122132','122231','113222','123122','123221','223211','221132',
  '221231','213212','223112','312131','311222','321122','321221','312212','322112','322211',
  '212123','212321','232121','111323','131123','131321','112313','132113','132311','211313',
  '231113','231311','112133','112331','132131','113123','113321','133121','313121','211331',
  '231131','213113','213311','213131','311123','311321','331121','312113','312311','332111',
  '314111','221411','431111','111224','111422','121124','121421','141122','141221','112214',
  '112412','122114','122411','142112','142211','241211','221114','413111','241112','134111',
  '111242','121142','121241','114212','124112','124211','411212','421112','421211','212141',
  '214121','412121','111143','111341','131141','114113','114311','411113','411311','113141',
  '114131','311141','411131','211412','211214','211232','2331112',
];

function sanitizeAsciiForCode128(text) {
  return String(text || '')
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
    .replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue')
    .replace(/ß/g, 'ss')
    .replace(/[^\x20-\x7E]/g, '');
}

/**
 * Erzeugt ein gestochen scharfes Code-128B Barcode-SVG als Data-URL (für Laser-, Ring- & Zebra-Scanner).
 */
export function buildCode128SvgDataUrl(rawText, { height = 56 } = {}) {
  const clean = sanitizeAsciiForCode128(rawText);
  if (!clean) return '';
  const codes = [104]; // Start Code B
  let checksum = 104;
  for (let i = 0; i < clean.length; i++) {
    const val = clean.charCodeAt(i) - 32;
    if (val < 0 || val > 94) continue;
    codes.push(val);
    checksum += val * (i + 1);
  }
  codes.push(checksum % 103);
  codes.push(106); // Stop

  const quietZone = 10;
  let x = quietZone;
  const rects = [];
  for (const c of codes) {
    const pat = CODE128_PATTERNS[c];
    if (!pat) continue;
    for (let i = 0; i < pat.length; i++) {
      const w = Number(pat[i]);
      if (i % 2 === 0) {
        rects.push(`<rect x="${x}" y="4" width="${w}" height="${height}" fill="#000"/>`);
      }
      x += w;
    }
  }
  const totalWidth = x + quietZone;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${totalWidth} ${height + 8}" width="100%" height="${height}"><rect width="100%" height="100%" fill="#fff"/>${rects.join('')}</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

const DEFAULT_PLACES = ['Vorne', 'Hinten', 'Sperrgut', 'A1', 'A2', 'A3', 'A4', 'A5', 'B1', 'B2', 'B3', 'B4', 'B5'];

function loadSavedPlaces() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch { /* ignore */ }
  return DEFAULT_PLACES;
}

/**
 * 🏷️ Regal-Barcodes & QR-Codes Generator (für Setup Schritt „Sortierung & Regale" sowie Wartung)
 */
export default function ShelfCodesModal({ onClose, colors = {}, language = 'de', inline = false }) {
  const tr = makeTr(language);
  const dark = !!colors.darkMode;

  const [places, setPlaces] = useState(loadSavedPlaces);
  const [codeMode, setCodeMode] = useState('both'); // 'both' | 'barcode' | 'qr'
  const [rowPrefix, setRowPrefix] = useState('P1');
  const [rowMode, setRowMode] = useState('letters'); // 'numbers' (1..N) | 'letters' (-A..-D)
  const [rowCount, setRowCount] = useState(4);
  const [freeInput, setFreeInput] = useState('');
  const [renderedItems, setRenderedItems] = useState([]);
  const printRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(places));
    } catch { /* ignore */ }
  }, [places]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = [];
      for (const p of places) {
        const cleanPlace = String(p || '').trim();
        if (!cleanPlace) continue;
        const scanValue = `RG:${cleanPlace}`;
        const barcodeUrl = buildCode128SvgDataUrl(scanValue, { height: 52 });
        let qrUrl = '';
        try {
          qrUrl = await QRCode.toDataURL(scanValue, {
            width: 130,
            margin: 1,
            errorCorrectionLevel: 'M',
          });
        } catch { /* ignore */ }
        list.push({
          label: cleanPlace,
          scanValue,
          barcodeUrl,
          qrUrl,
        });
      }
      if (!cancelled) setRenderedItems(list);
    })();
    return () => { cancelled = true; };
  }, [places]);

  const addUniquePlaces = (newItems) => {
    setPlaces((prev) => {
      const existingLower = new Set(prev.map((x) => x.toLowerCase()));
      const next = [...prev];
      for (const item of newItems) {
        const clean = String(item || '').trim();
        if (!clean || existingLower.has(clean.toLowerCase())) continue;
        existingLower.add(clean.toLowerCase());
        next.push(clean);
      }
      return next;
    });
  };

  const handleAddPreset = (type) => {
    if (type === 'zones') {
      addUniquePlaces(['Vorne', 'Hinten', 'Lager', 'Sperrgut', 'Kasse', 'Boden']);
    } else if (type === 'grenzpaket') {
      const gp = ['Vorne', 'Hinten', 'Sperrgut'];
      for (let p = 1; p <= 6; p++) {
        for (const sub of ['A', 'B', 'C', 'D']) {
          gp.push(`P${p}-${sub}`);
        }
      }
      for (let k = 1; k <= 4; k++) {
        for (const sub of ['A', 'B', 'C', 'D']) {
          gp.push(`K${k}-${sub}`);
        }
      }
      setPlaces(gp);
    } else if (type === 'abc') {
      const abc = [];
      for (const r of ['A', 'B', 'C']) {
        for (let i = 1; i <= 10; i++) abc.push(`${r}${i}`);
      }
      setPlaces(abc);
    } else if (type === 'numbers') {
      const nums = [];
      for (let i = 1; i <= 20; i++) nums.push(`F${i}`);
      setPlaces(nums);
    }
  };

  const handleAddRow = () => {
    const pfx = String(rowPrefix || '').trim().toUpperCase();
    if (!pfx) return;
    const count = Math.max(1, Math.min(50, Number(rowCount) || 4));
    const generated = [];
    if (rowMode === 'letters') {
      const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      for (let i = 0; i < Math.min(count, 26); i++) {
        generated.push(`${pfx}-${letters[i]}`);
      }
    } else {
      for (let i = 1; i <= count; i++) {
        generated.push(`${pfx}${i}`);
      }
    }
    addUniquePlaces(generated);
  };

  const handleAddFreeInput = (e) => {
    if (e) e.preventDefault();
    if (!freeInput.trim()) return;
    const parts = freeInput
      .split(/[,;\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    addUniquePlaces(parts);
    setFreeInput('');
  };

  const removePlace = (target) => {
    setPlaces((prev) => prev.filter((p) => p !== target));
  };

  const handlePrint = async () => {
    if (!printRef.current || renderedItems.length === 0) return;
    const htmlBody = printRef.current.innerHTML;
    const docHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Regal-Barcodes & QR-Codes</title>
  <style>
    @page { size: A4 portrait; margin: 10mm; }
    body { font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 0; color: #000; background: #fff; }
    .sheet-grid {
      display: grid;
      grid-template-columns: repeat(${codeMode === 'qr' ? 4 : 2}, 1fr);
      gap: 10px;
    }
    .shelf-card {
      border: 2px solid #000;
      border-radius: 8px;
      padding: 10px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      page-break-inside: avoid;
      background: #fff;
    }
    .shelf-title {
      font-size: 26px;
      font-weight: 900;
      letter-spacing: 0.5px;
      margin-bottom: 6px;
      text-align: center;
    }
    .shelf-codes-row {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      width: 100%;
    }
    .shelf-sub {
      font-size: 11px;
      color: #444;
      margin-top: 4px;
      font-family: monospace;
    }
  </style>
</head>
<body>
  <div class="sheet-grid">${htmlBody}</div>
</body>
</html>`;

    if (window.Capacitor?.isNativePlatform()) {
      try {
        const { Filesystem, Directory } = await import('@capacitor/filesystem');
        const { Share } = await import('@capacitor/share');
        const res = await Filesystem.writeFile({
          path: `regal-barcodes-${Date.now()}.html`,
          data: docHtml,
          directory: Directory.Cache,
          encoding: 'utf8',
        });
        await Share.share({
          title: 'Regal-Barcodes & QR-Codes',
          text: 'Regal-Liste zum Ausdrucken',
          url: res.uri,
        });
        return;
      } catch (err) {
        alert(err?.message || 'Teilen fehlgeschlagen');
        return;
      }
    }

    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(docHtml);
    w.document.close();
    setTimeout(() => {
      try { w.focus(); w.print(); } catch { /* ignore */ }
    }, 250);
  };

  const boxStyle = {
    padding: 12,
    borderRadius: 10,
    border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
    background: dark ? '#1b202b' : '#f8fafc',
  };

  const content = (
    <div
      data-testid="shelf-codes-generator"
      style={{
        width: '100%',
        maxWidth: inline ? 920 : 680,
        maxHeight: inline ? 'none' : '93vh',
        overflowY: inline ? 'visible' : 'auto',
        borderRadius: 14,
        background: dark ? '#141821' : '#ffffff',
        color: colors.text || (dark ? '#f8fafc' : '#1e293b'),
        boxShadow: inline ? 'none' : '0 20px 50px rgba(0,0,0,0.45)',
        padding: inline ? 0 : 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
            🏷️ {tr('Regal-Liste: Barcodes & QR-Codes erstellen', 'Shelf List: Generate Barcodes & QR Codes', 'Stellinglijst: Barcodes & QR-codes maken')}
          </h2>
          <div style={{ fontSize: 12.5, color: dark ? '#94a3b8' : '#64748b', marginTop: 2 }}>
            {tr(
              'Erstelle deine Regalfächer und Lagerzonen (z. B. Vorne, Hinten, P1-A bis P6-C) als druckbare Liste für Lagerscanner (Zebra TC75 / Ringscanner) & Handy-Kamera.',
              'Create your shelf slots and storage zones as a printable sheet for warehouse scanners (Zebra TC75 / ring scanner) & phone cameras.',
              'Maak je stellingvakken en magazijnzones als printbare lijst voor magazijnscanners & telefooncamera.'
            )}
          </div>
        </div>
        {onClose && (
          <button
            type="button"
            data-testid="shelf-codes-close"
            onClick={onClose}
            style={{
              padding: '7px 12px',
              borderRadius: 8,
              border: 'none',
              background: dark ? '#334155' : '#e2e8f0',
              color: dark ? '#fff' : '#1e293b',
              fontSize: 14,
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        )}
      </div>

      {/* 1. Schnell-Vorlagen */}
      <div style={boxStyle}>
        <strong style={{ fontSize: 13.5, display: 'block', marginBottom: 6 }}>
          ⚡ {tr('1. Schnell-Vorlagen (1 Klick)', '1. Quick Templates (1 Click)', '1. Snelle sjablonen (1 klik)')}
        </strong>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button
            type="button"
            data-testid="shelf-preset-zones"
            onClick={() => handleAddPreset('zones')}
            style={{
              padding: '7px 11px', borderRadius: 8, border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
              background: dark ? '#0f172a' : '#fff', color: dark ? '#f8fafc' : '#1e293b', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
            }}
          >
            🏠 + {tr('Grobe Zonen (Vorne, Hinten, Sperrgut…)', 'Rough Zones (Front, Back, Bulky…)', 'Grove zones (Voor, Achter…)')}
          </button>
          <button
            type="button"
            data-testid="shelf-preset-grenzpaket"
            onClick={() => handleAddPreset('grenzpaket')}
            style={{
              padding: '7px 11px', borderRadius: 8, border: '1.5px solid #dc2626',
              background: dark ? '#450a0a' : '#fef2f2', color: dark ? '#fca5a5' : '#991b1b', fontSize: 12.5, fontWeight: 800, cursor: 'pointer',
            }}
          >
            🇨🇭 {tr('Grenzpaket-Vorlage (P1-A…P6-D, K1-A…K4-D)', 'Grenzpaket Template (P1-A…P6-D)', 'Grenzpaket-sjabloon')}
          </button>
          <button
            type="button"
            data-testid="shelf-preset-abc"
            onClick={() => handleAddPreset('abc')}
            style={{
              padding: '7px 11px', borderRadius: 8, border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
              background: dark ? '#0f172a' : '#fff', color: dark ? '#f8fafc' : '#1e293b', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
            }}
          >
            🔤 {tr('Regale A1–A10, B1–B10, C1–C10', 'Shelves A1–A10, B1–B10, C1–C10', 'Stellingen A1–A10, B1–B10, C1–C10')}
          </button>
        </div>
      </div>

      {/* 2. Eigene Regal-Reihe oder freie Namen hinzufügen */}
      <div style={boxStyle}>
        <strong style={{ fontSize: 13.5, display: 'block', marginBottom: 8 }}>
          ➕ {tr('2. Eigene Regal-Reihe oder einzelne Plätze ergänzen', '2. Add Custom Shelf Row or Single Slots', '2. Eigen stellingrij of losse plaatsen toevoegen')}
        </strong>

        {/* Reihe hinzufügen */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
          <input
            type="text"
            data-testid="shelf-row-prefix"
            value={rowPrefix}
            onChange={(e) => setRowPrefix(e.target.value.toUpperCase())}
            placeholder="z.B. P7 oder D"
            maxLength={6}
            style={{
              width: 90, padding: '7px 9px', borderRadius: 7, border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
              background: dark ? '#0f172a' : '#fff', color: dark ? '#fff' : '#1e293b', fontSize: 13, fontWeight: 700,
            }}
          />
          <select
            data-testid="shelf-row-mode"
            value={rowMode}
            onChange={(e) => setRowMode(e.target.value)}
            style={{
              padding: '7px 9px', borderRadius: 7, border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
              background: dark ? '#0f172a' : '#fff', color: dark ? '#fff' : '#1e293b', fontSize: 13, fontWeight: 600,
            }}
          >
            <option value="letters">{tr('Unterfächer -A, -B, -C…', 'Sub-slots -A, -B, -C…', 'Subvakken -A, -B, -C…')}</option>
            <option value="numbers">{tr('Nummern 1, 2, 3…', 'Numbers 1, 2, 3…', 'Nummers 1, 2, 3…')}</option>
          </select>
          <input
            type="number"
            min="1"
            max="50"
            value={rowCount}
            onChange={(e) => setRowCount(parseInt(e.target.value, 10) || 1)}
            style={{
              width: 58, padding: '7px 8px', borderRadius: 7, border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
              background: dark ? '#0f172a' : '#fff', color: dark ? '#fff' : '#1e293b', fontSize: 13, fontWeight: 700,
            }}
          />
          <button
            type="button"
            data-testid="shelf-add-row-btn"
            onClick={handleAddRow}
            style={{
              padding: '7px 12px', borderRadius: 7, border: 'none',
              background: '#2563eb', color: '#fff', fontSize: 12.5, fontWeight: 800, cursor: 'pointer',
            }}
          >
            ➕ {tr('Reihe anlegen', 'Add Row', 'Rij toevoegen')}
          </button>
        </div>

        {/* Freie Eingabe */}
        <form onSubmit={handleAddFreeInput} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <input
            type="text"
            data-testid="shelf-free-input"
            value={freeInput}
            onChange={(e) => setFreeInput(e.target.value)}
            placeholder={tr('Einzelne Plätze (kommagetrennt, z. B. Vorne, Hinten, P6-C, Keller)', 'Single places (comma-separated, e.g. Front, Back, P6-C)', 'Losse plaatsen (kommagescheiden)')}
            style={{
              flex: '1 1 220px', padding: '7px 10px', borderRadius: 7, border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
              background: dark ? '#0f172a' : '#fff', color: dark ? '#fff' : '#1e293b', fontSize: 13,
            }}
          />
          <button
            type="submit"
            data-testid="shelf-add-free-btn"
            style={{
              padding: '7px 12px', borderRadius: 7, border: 'none',
              background: '#16a34a', color: '#fff', fontSize: 12.5, fontWeight: 800, cursor: 'pointer',
            }}
          >
            ➕ {tr('Hinzufügen', 'Add', 'Toevoegen')}
          </button>
        </form>
      </div>

      {/* 3. Aktuelle Regal-Liste (Chips) & Code-Typ */}
      <div style={boxStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <strong style={{ fontSize: 13.5 }}>
            📋 {tr(`Deine Regal-Liste (${places.length} Plätze)`, `Your Shelf List (${places.length} slots)`, `Jouw stellinglijst (${places.length} vakken)`)}
          </strong>
          {places.length > 0 && (
            <button
              type="button"
              data-testid="shelf-clear-all"
              onClick={() => setPlaces([])}
              style={{
                padding: '4px 9px', borderRadius: 6, border: 'none',
                background: '#ef4444', color: '#fff', fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
              }}
            >
              🗑️ {tr('Liste leeren', 'Clear list', 'Lijst wissen')}
            </button>
          )}
        </div>

        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', maxHeight: 115, overflowY: 'auto', paddingBottom: 4 }}>
          {places.map((p) => (
            <span
              key={p}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: 999,
                background: dark ? '#0f172a' : '#e2e8f0', color: dark ? '#f8fafc' : '#1e293b', fontSize: 12, fontWeight: 700,
              }}
            >
              <span>{p}</span>
              <button
                type="button"
                onClick={() => removePlace(p)}
                style={{ border: 'none', background: 'transparent', color: '#ef4444', fontWeight: 900, cursor: 'pointer', padding: 0, fontSize: 12 }}
              >
                ×
              </button>
            </span>
          ))}
        </div>

        {/* Format-Auswahl */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 10, paddingTop: 8, borderTop: `1px solid ${dark ? '#334155' : '#e2e8f0'}` }}>
          <span style={{ fontSize: 12, fontWeight: 700 }}>{tr('Code-Format:', 'Code Format:', 'Code-formaat:')}</span>
          {[
            ['both', tr('📊 Strichcode + 🔳 QR-Code (Empfohlen)', '📊 Barcode + 🔳 QR Code (Recommended)', '📊 Streepjescode + 🔳 QR-code')],
            ['barcode', tr('📊 Nur Strichcode (Zebra / Laser-Scanner)', '📊 Barcode only (Zebra / Laser)', '📊 Alleen streepjescode')],
            ['qr', tr('🔳 Nur QR-Code (Kompakt-Bogen)', '🔳 QR Code only (Compact)', '🔳 Alleen QR-code')],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              data-testid={`shelf-codemode-${id}`}
              onClick={() => setCodeMode(id)}
              style={{
                padding: '5px 10px', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                border: `1.5px solid ${codeMode === id ? '#2563eb' : (dark ? '#475569' : '#cbd5e1')}`,
                background: codeMode === id ? '#2563eb' : 'transparent',
                color: codeMode === id ? '#fff' : (dark ? '#f8fafc' : '#1e293b'),
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* 4. Drucken-Button + Druck-Vorschau */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, color: dark ? '#94a3b8' : '#64748b' }}>
          💡 {tr(
            'Jeder Code enthält das Präfix RG: (z. B. RG:P6-C), damit jeder Scanner sofort erkennt, dass es ein Regalplatz ist.',
            'Each code includes the RG: prefix (e.g. RG:P6-C) so scanners immediately recognize it as a shelf.',
            'Elke code bevat het voorvoegsel RG: zodat scanners direct herkennen dat het een stellingvak is.'
          )}
        </span>
        <button
          type="button"
          data-testid="shelf-print-sheet-btn"
          disabled={renderedItems.length === 0}
          onClick={handlePrint}
          style={{
            padding: '10px 18px',
            borderRadius: 10,
            border: 'none',
            background: '#16a34a',
            color: '#fff',
            fontSize: 14,
            fontWeight: 800,
            cursor: renderedItems.length === 0 ? 'not-allowed' : 'pointer',
            opacity: renderedItems.length === 0 ? 0.5 : 1,
          }}
        >
          🖨️ {tr(`Regal-Liste drucken (${renderedItems.length})`, `Print Shelf Sheet (${renderedItems.length})`, `Stellinglijst printen (${renderedItems.length})`)}
        </button>
      </div>

      {/* Vorschau-Bogen */}
      <div
        ref={printRef}
        style={{
          display: 'grid',
          gridTemplateColumns: codeMode === 'qr' ? 'repeat(auto-fill, minmax(120px, 1fr))' : 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: 10,
          maxHeight: inline ? 420 : 260,
          overflowY: 'auto',
          padding: 10,
          borderRadius: 10,
          background: '#ffffff',
          color: '#000000',
          border: '1px solid #cbd5e1',
        }}
      >
        {renderedItems.map((item) => (
          <div
            key={item.label}
            className="shelf-card"
            style={{
              border: '2px solid #000',
              borderRadius: 8,
              padding: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: '#fff',
            }}
          >
            <div className="shelf-title" style={{ fontSize: 22, fontWeight: 900, marginBottom: 4, color: '#000' }}>
              {item.label}
            </div>
            <div className="shelf-codes-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%' }}>
              {(codeMode === 'both' || codeMode === 'barcode') && item.barcodeUrl && (
                <img src={item.barcodeUrl} alt={`Barcode ${item.label}`} style={{ height: 48, maxWidth: codeMode === 'both' ? 155 : 210 }} />
              )}
              {(codeMode === 'both' || codeMode === 'qr') && item.qrUrl && (
                <img src={item.qrUrl} alt={`QR ${item.label}`} style={{ width: codeMode === 'both' ? 58 : 92, height: codeMode === 'both' ? 58 : 92 }} />
              )}
            </div>
            <div className="shelf-sub" style={{ fontSize: 10.5, color: '#444', marginTop: 3, fontFamily: 'monospace' }}>
              {item.scanValue}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (inline) return content;

  return (
    <div
      data-testid="shelf-codes-modal"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 12,
      }}
      onClick={onClose}
    >
      {content}
    </div>
  );
}

